import { Injectable } from "@angular/core";

import { firstValueFrom, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { UmbracoHttpClient } from "@e-forsyning/common/http";
import { UmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger/model/umbraco-indstillinger.model";

import { getServiceProConsentVersion } from "./servicepro-cookies.registry";

const VERSION_PATTERN = /(\d+\.\d+(?:\.\d+)?)/;

@Injectable({ providedIn: "root" })
export class ServiceProCookieConsentVersionService {
    constructor(private readonly umbracoHttpClient: UmbracoHttpClient) {}

    public resolvePolicyNodeId(settings: UmbracoIndstillinger | undefined): number | undefined {
        if (!settings) {
            return undefined;
        }
        return (
            settings.aktuelEforsyningNodeId ||
            settings.aktuelEforsyning?.nodeId ||
            settings.forsyningWeb?.nodeId ||
            settings.vaerkNodeId ||
            undefined
        );
    }

    public async resolveConsentVersion(settings: UmbracoIndstillinger | undefined): Promise<string> {
        const nodeId = this.resolvePolicyNodeId(settings);
        if (!nodeId) {
            return getServiceProConsentVersion();
        }

        try {
            const html = await firstValueFrom(this.umbracoHttpClient.get<string>(`GetCookieConsentText?nodeId=${nodeId}`));
            const parsed = this.parseVersionFromPolicyHtml(html);
            return parsed ?? getServiceProConsentVersion();
        } catch {
            return getServiceProConsentVersion();
        }
    }

    private parseVersionFromPolicyHtml(html: string): string | undefined {
        if (!html) {
            return undefined;
        }
        const match = html.match(VERSION_PATTERN);
        return match?.[1];
    }
}

export function isServiceProCookieConsentEnabled(settings: UmbracoIndstillinger | undefined): boolean {
    if (!settings) {
        return true;
    }

    const hasSiteContext = !!(settings.aktuelEforsyningNodeId || settings.forsyningWeb?.nodeId || settings.vaerkNodeId);
    if (!hasSiteContext) {
        // ServicePro login før værk/Umbraco-hydration: vis consent indtil site-kontekst er kendt.
        return true;
    }

    return settings.featureToggles?.cookiepolitik ?? true;
}
